import { sel } from "../selectors";
import { DEFAULT_STAGE_VIEWPORT, Scene } from "./types";

// Lesson: Getting started → Welcome. A light tour of the workspace on the
// base fixture (`firstNarrativeReport`: two finished cases). Nothing is
// navigated into; the Academy item is only pointed at.
const scene: Scene = {
  id: "welcome-to-police-narratives-ai",
  title: "Welcome",
  startRoute: "/cases",
  fixture: "firstNarrativeReport",
  viewport: DEFAULT_STAGE_VIEWPORT,
  steps: [
    {
      id: "intro",
      narration:
        "Welcome to Police Narratives AI. You record or upload audio, the AI drafts the narrative in your style, and you review it in the editor. This is a quick tour of where everything lives.",
      actions: [
        { type: "waitFor", target: sel("newCaseDropdown") },
        { type: "waitFor", target: sel("caseListItem") },
        { type: "spotlight", target: null },
        { type: "wait", ms: 1500 },
      ],
    },
    {
      id: "new-case",
      narration:
        "Everything starts with New Case at the top of the sidebar. It opens a menu: Record Audio, Upload Files, and Blank Report. If your department has checklist templates, Checklist shows up here too.",
      actions: [
        { type: "moveTo", target: `${sel("newCaseDropdown")} button` },
        { type: "wait", ms: 1800 },
        { type: "click", target: `${sel("newCaseDropdown")} button` },
        { type: "waitFor", target: sel("newCaseUpload") },
        { type: "wait", ms: 8300 },
        // The dropdown closes when it loses focus, so click the Cases item.
        { type: "click", target: `${sel("casesNav")} button` },
        { type: "wait", ms: 300 },
      ],
    },
    {
      id: "preferences",
      narration:
        "The sliders icon beside it opens Report Preferences, the page that controls how your drafts are written.",
      actions: [
        { type: "moveTo", target: sel("preferencesButton") },
        { type: "spotlight", target: sel("preferencesButton") },
        { type: "wait", ms: 4800 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "cases",
      narration:
        "Cases is your list of reports, and the search box finds a report by title or content. Trash holds deleted cases, and you can also drag a case onto it.",
      actions: [
        { type: "moveTo", target: sel("casesNav") },
        { type: "spotlight", target: sel("casesNav") },
        { type: "wait", ms: 1700 },
        { type: "moveTo", target: sel("casesSearch") },
        { type: "spotlight", target: sel("casesSearch") },
        { type: "wait", ms: 2800 },
        { type: "moveTo", target: sel("trashNav") },
        { type: "spotlight", target: sel("trashNav") },
        { type: "wait", ms: 3000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "areas",
      narration:
        "Files, Forms, and Redactions are the other work areas. Investigations, Policies, and Feature Requests only appear when your account has access to them. And Academy, where you're watching this, is right below.",
      actions: [
        { type: "moveTo", target: sel("filesNav") },
        { type: "spotlight", target: sel("filesNav") },
        { type: "wait", ms: 700 },
        { type: "moveTo", target: sel("formsNav") },
        { type: "spotlight", target: sel("formsNav") },
        { type: "wait", ms: 700 },
        { type: "moveTo", target: sel("redactionsNav") },
        { type: "spotlight", target: sel("redactionsNav") },
        { type: "wait", ms: 5600 },
        { type: "moveTo", target: sel("academyNav") },
        { type: "spotlight", target: sel("academyNav") },
        { type: "wait", ms: 2600 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "footer",
      narration:
        "Below those are your categories, starting with Favorites. The footer shows your name and badge number, a switch for dark mode, and a gear that opens Settings.",
      actions: [
        { type: "moveTo", target: sel("caseCategories") },
        { type: "spotlight", target: sel("caseCategories") },
        { type: "wait", ms: 3200 },
        { type: "moveTo", target: sel("settingsButton") },
        { type: "spotlight", target: sel("settingsButton") },
        { type: "wait", ms: 4800 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "settings-vs-preferences",
      narration:
        "Settings is about your account: name, email, password, plan, and appearance. Report Preferences is about your writing. If a draft comes out in the wrong perspective or time format, the fix is in Report Preferences, not Settings.",
      actions: [
        { type: "moveTo", target: sel("settingsButton") },
        { type: "spotlight", target: sel("settingsButton") },
        { type: "wait", ms: 5000 },
        { type: "moveTo", target: sel("preferencesButton") },
        { type: "spotlight", target: sel("preferencesButton") },
        { type: "wait", ms: 7500 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "editor",
      narration:
        "Open any case and the narrative is on the left. The right panel has tabs for Suggestions, Snippets, Statutes, and Redactions.",
      actions: [
        { type: "moveTo", target: sel("caseListItem") },
        { type: "wait", ms: 500 },
        { type: "click", target: sel("caseListItem") },
        { type: "waitFor", target: sel("editorSidebarTabs") },
        { type: "wait", ms: 800 },
        { type: "moveTo", target: sel("editorSidebarTabs") },
        { type: "spotlight", target: sel("editorSidebarTabs") },
        { type: "wait", ms: 4000 },
        { type: "spotlight", target: null },
      ],
    },
    {
      id: "outro",
      narration:
        "Next, set up your report preferences so your first draft comes out in your voice.",
      actions: [{ type: "moveTo", target: sel("editorNarrative") }, { type: "wait", ms: 1500 }],
    },
  ],
};

export default scene;
